import { Link } from "react-router-dom";
import {
  ArrowRight,
  BellRing,
  CalendarDays,
  Check,
  FileSignature,
  Heart,
  Languages,
  ListChecks,
  Palette,
  Sparkles,
  Users,
  WalletCards,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { APP_NAME } from "@/lib/constants";

const features = [
  { icon: CalendarDays, es: "Calendario y cronograma", en: "Calendar and timeline" },
  { icon: Users, es: "Invitados y confirmaciones", en: "Guests and RSVPs" },
  { icon: WalletCards, es: "Presupuestos y pagos", en: "Budgets and payments" },
  { icon: FileSignature, es: "Proveedores y contratos", en: "Vendors and contracts" },
  { icon: ListChecks, es: "Tareas y programa del día", en: "Tasks and run of show" },
  { icon: Palette, es: "Inspiración y documentos", en: "Inspiration and documents" },
];

const plans = [
  { name: "Individual", price: 399, events: 10, seats: 1 },
  { name: "Studio", price: 899, events: 40, seats: 5, featured: true },
  { name: "Agency", price: 1899, events: 150, seats: 15 },
];

export function LandingPage() {
  const { language, setLanguage } = useLanguage();
  const es = language === "es";

  return (
    <div className="min-h-screen overflow-hidden bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5" aria-label={APP_NAME}>
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Heart className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="font-display text-lg font-semibold">{APP_NAME}</span>
          </Link>
          <nav className="hidden items-center gap-7 text-sm text-muted-foreground md:flex">
            <a href="#platform" className="transition-colors hover:text-foreground">{es ? "Plataforma" : "Platform"}</a>
            <a href="#workflow" className="transition-colors hover:text-foreground">{es ? "Cómo funciona" : "How it works"}</a>
            <a href="#pricing" className="transition-colors hover:text-foreground">{es ? "Precios" : "Pricing"}</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(es ? "en" : "es")}
              aria-label={es ? "Cambiar a inglés" : "Switch to Spanish"}
            >
              <Languages className="h-4 w-4" /> {es ? "EN" : "ES"}
            </Button>
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/login">{es ? "Iniciar sesión" : "Sign in"}</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/signup">{es ? "Crear cuenta" : "Create account"}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative isolate overflow-hidden border-b">
          <img
            src="/images/editorial/hacienda-banquet.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 -z-30 h-full w-full object-cover object-center opacity-80"
          />
          <div className="absolute inset-0 -z-20 bg-gradient-to-r from-background via-background/80 to-background/10" />
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_78%_18%,hsl(var(--accent)/0.18),transparent_32%),radial-gradient(circle_at_18%_76%,hsl(var(--primary)/0.12),transparent_38%)]" />
          <div className="container grid min-h-[720px] items-center gap-14 py-20 lg:grid-cols-[1.05fr_.95fr] lg:py-28">
            <div className="max-w-2xl animate-fade-in">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-surface/80 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-xs">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                {es ? "Creado para planners profesionales" : "Built for professional planners"}
              </div>
              <h1 className="font-display text-5xl font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                {es ? "Cada boda en orden. Tu negocio en control." : "Every wedding organized. Your business in control."}
              </h1>
              <p className="mt-7 max-w-xl text-lg leading-8 text-muted-foreground">
                {es
                  ? "Atelier Suite reúne clientes, invitados, presupuesto, proveedores, contratos y tiempos en un solo espacio de trabajo para tu equipo."
                  : "Atelier Suite brings clients, guests, budgets, vendors, contracts, and timelines into one workspace for your team."}
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg" className="h-12 px-7">
                  <Link to="/signup">{es ? "Empezar ahora" : "Start now"}<ArrowRight /></Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-7 bg-background/50">
                  <Link to="/login">{es ? "Entrar a mi espacio" : "Open my workspace"}</Link>
                </Button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                {es ? "Prueba la plataforma durante la beta. No requiere tarjeta." : "Try the platform during beta. No card required."}
              </p>
            </div>

            <div className="relative mx-auto w-full max-w-xl lg:mx-0">
              <div className="overflow-hidden rounded-2xl border bg-card shadow-overlay">
                <div className="flex items-center justify-between border-b px-5 py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-primary">{es ? "Próxima boda" : "Next wedding"}</p>
                    <p className="mt-1 font-display text-2xl font-semibold">Mariana &amp; Javier</p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{es ? "Planeación" : "Planning"}</span>
                </div>
                <div className="grid grid-cols-3 divide-x border-b bg-muted/30 text-center">
                  {[es ? "42 días" : "42 days", "186", "MX$640k"].map((value, index) => (
                    <div key={value} className="px-3 py-5">
                      <p className="text-lg font-semibold">{value}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">{[es ? "Restantes" : "Remaining", es ? "Invitados" : "Guests", es ? "Presupuesto" : "Budget"][index]}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-3 p-5">
                  {[{ label: es ? "Confirmaciones recibidas" : "RSVPs received", value: "72%" }, { label: es ? "Presupuesto contratado" : "Budget booked", value: "64%" }, { label: es ? "Tareas completadas" : "Tasks completed", value: "81%" }].map((item, index) => (
                    <div key={item.label} className="rounded-xl border bg-surface-raised p-4">
                      <div className="mb-2 flex items-center justify-between text-sm"><span>{item.label}</span><strong>{item.value}</strong></div>
                      <div className="h-1.5 rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: item.value }} /></div>
                      {index === 2 && <div className="mt-3 flex items-center gap-2 text-xs text-success"><BellRing className="h-3.5 w-3.5" />{es ? "Todo a tiempo esta semana" : "Everything is on track this week"}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="platform" className="container py-24 sm:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{es ? "Una sola plataforma" : "One platform"}</p>
            <h2 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">{es ? "Menos pestañas. Más claridad." : "Fewer tabs. More clarity."}</h2>
            <p className="mt-5 text-muted-foreground">{es ? "Cada módulo comparte la misma boda, equipo y permisos para que la información siempre esté conectada." : "Every module shares the same wedding, team, and permissions, so information stays connected."}</p>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => <div key={feature.en} className="group rounded-xl border bg-card p-6 shadow-card transition hover:-translate-y-1 hover:shadow-raised"><feature.icon className="h-5 w-5 text-primary" /><h3 className="mt-5 text-base font-semibold">{es ? feature.es : feature.en}</h3><p className="mt-2 text-sm leading-6 text-muted-foreground">{es ? "Información centralizada y disponible para las personas correctas." : "Centralized information, available to the right people."}</p></div>)}
          </div>
        </section>

        <section id="workflow" className="border-y bg-primary text-primary-foreground">
          <div className="container grid gap-12 py-24 lg:grid-cols-[.8fr_1.2fr] lg:items-center">
            <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/65">{es ? "De consulta a celebración" : "From inquiry to celebration"}</p><h2 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">{es ? "Un flujo que acompaña tu manera de trabajar." : "A workflow that follows the way you work."}</h2></div>
            <div className="grid gap-8 sm:grid-cols-3">
              {[{ n: "01", es: "Crea la organización y registra cada boda.", en: "Create your organization and add every wedding." }, { n: "02", es: "Coordina equipo, proveedores, presupuesto e invitados.", en: "Coordinate team, vendors, budget, and guests." }, { n: "03", es: "Llega al evento con pendientes y tiempos bajo control.", en: "Arrive at event day with tasks and timing under control." }].map((step) => <div key={step.n}><span className="font-mono text-xs text-primary-foreground/55">{step.n}</span><p className="mt-4 leading-7 text-primary-foreground/85">{es ? step.es : step.en}</p></div>)}
            </div>
          </div>
        </section>

        <section id="pricing" className="container py-24 sm:py-28">
          <div className="mx-auto max-w-2xl text-center"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{es ? "Planes para crecer" : "Plans built to grow"}</p><h2 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">{es ? "Empieza con lo que necesitas hoy." : "Start with what you need today."}</h2><p className="mt-5 text-muted-foreground">{es ? "Precios de referencia. Durante la beta podrás probar Atelier Suite sin cargo." : "Reference pricing. During beta, you can try Atelier Suite at no charge."}</p></div>
          <div className="mx-auto mt-14 grid max-w-5xl gap-5 lg:grid-cols-3">
            {plans.map((plan) => <div key={plan.name} className={`relative rounded-2xl border p-7 ${plan.featured ? "border-primary bg-primary/[0.035] shadow-raised" : "bg-card shadow-card"}`}>{plan.featured && <span className="absolute right-5 top-5 rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">{es ? "Recomendado" : "Recommended"}</span>}<h3 className="font-display text-2xl font-semibold">{plan.name}</h3><p className="mt-6 text-4xl font-semibold">MX${plan.price.toLocaleString("es-MX")}<span className="text-sm font-normal text-muted-foreground"> / {es ? "mes" : "month"}</span></p><div className="mt-7 space-y-3 text-sm"><p className="flex gap-2"><Check className="text-success" />{plan.seats} {es ? plan.seats === 1 ? "usuario" : "usuarios" : plan.seats === 1 ? "user" : "users"}</p><p className="flex gap-2"><Check className="text-success" />{es ? `Hasta ${plan.events} eventos activos` : `Up to ${plan.events} active events`}</p><p className="flex gap-2"><Check className="text-success" />{es ? "Todos los módulos incluidos" : "All modules included"}</p></div><Button asChild className="mt-8 w-full" variant={plan.featured ? "default" : "outline"}><Link to="/signup">{es ? "Probar en beta" : "Try the beta"}</Link></Button></div>)}
          </div>
        </section>

        <section className="container pb-24">
          <div className="relative isolate overflow-hidden rounded-2xl px-7 py-20 text-center text-white shadow-overlay sm:px-12">
            <img
              src="/images/editorial/dance-floor.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/55 via-black/35 to-black/55" />
            <h2 className="font-display text-4xl font-semibold">{es ? "Planea con calma. Opera con precisión." : "Plan calmly. Operate precisely."}</h2><p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-white/85">{es ? "Crea tu espacio y reúne el trabajo de cada boda en un sistema pensado para planners." : "Create your workspace and bring every wedding into a system designed for planners."}</p><Button asChild size="lg" className="mt-8 bg-white !text-black hover:bg-white/90"><Link to="/signup">{es ? "Crear mi espacio" : "Create my workspace"}<ArrowRight /></Link></Button>
          </div>
        </section>
      </main>

      <footer className="border-t">
        <div className="container flex flex-col gap-4 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between"><p>© {new Date().getFullYear()} {APP_NAME}</p><div className="flex gap-5"><Link to="/login" className="hover:text-foreground">{es ? "Iniciar sesión" : "Sign in"}</Link><a href="mailto:hola@ateliersuite.app" className="hover:text-foreground">{es ? "Contacto" : "Contact"}</a></div></div>
      </footer>
    </div>
  );
}
